import { CombinedQualification } from '../../../shared/interfaces/qualification.interface';
import { NestingQualification } from '../../../shared/interfaces/quota-nesting.interface';
import {
  Criteria,
  NestedQuota,
  QuotaPayload,
} from '../../../shared/interfaces/quota.interface';
import { RandomStringGenerator } from '../../../shared/utils/random-string-generator';
import { NestedQualificationBuilder } from '../../domain/nested-qualification-builder';
import { NestedQuotaPercentageMechanic } from '../../domain/nested-quota-percentage-mechanic';
import { SurveyNestedQuotas } from '../../domain/survey-nested-quotas';
import { SurveyQuotaCriterion } from '../../domain/survey-quota-criterion';
import {
  EachNestingGroupData,
  NestingGroupState,
  NestingGroupTitleState,
} from '../interfaces/multi-quota-nesting.interface';
import { NestedQuotaCombinedKeyBuilder } from './nested-quota-combine-key-builder';
import { NestedQuotasResetEvaluator } from './nested-quotas-reset-evaluator';

export class NestingGroupDataBuilder {
  private _selectedQualifications: NestingQualification[] = [];
  constructor(
    private readonly _nestedQuotas: NestedQuota[],
    private readonly _surveyQualifications: CombinedQualification[],
    private readonly _surveyQuotas: QuotaPayload[],
    private readonly _surveyAllocation: number,
    private readonly _groupId: string
  ) {}

  private get _surveyNestedQuotas(): SurveyNestedQuotas {
    return new SurveyNestedQuotas(this._nestedQuotas).kind(this._groupId);
  }

  private get _selectedIds(): number[] {
    return this._selectedQualifications.map((q) =>
      Number(q.qualification_code)
    );
  }

  public build(): EachNestingGroupData {
    this._selectedQualifications = this._getSelectedQualifications();
    return {
      uniqueId: new RandomStringGenerator(6).generate(),
      id: this._groupId,
      state: this._state(),
      quotas: this._mappedQuotas(),
      qualifications: this._qualifications(),
      selectedQualifications: this._selectedQualifications,
      titleState: this._titleState(),
      qualificationSelection: this._qualSelectable(),
      canResetTable: this._canResetTable(),
    };
  }

  private _state(): NestingGroupState {
    return this._surveyNestedQuotas.qualificationIds().length
      ? 'created'
      : 'pending';
  }

  private _mappedQuotas(): NestedQuota[] {
    const mappedQuotas = this._surveyNestedQuotas.get().map((nestedQuota) => {
      if (nestedQuota.individual_quotas?.length) {
        return {
          ...nestedQuota,
          isQuotaGrouped: true,
          isChecked: false,
          individual_quotas: nestedQuota.individual_quotas.map((quota) => ({
            ...quota,
            criteria: this._sortCriterias(quota.criteria),
            combinedKey: new NestedQuotaCombinedKeyBuilder(
              quota.criteria,
              this._selectedQualifications
            ).build(),
          })),
        };
      }
      return {
        ...nestedQuota,
        criteria: this._sortCriterias(nestedQuota.criteria),
        combinedKey: new NestedQuotaCombinedKeyBuilder(
          nestedQuota.criteria,
          this._selectedQualifications
        ).build(),
      };
    });
    return new NestedQuotaPercentageMechanic(
      mappedQuotas,
      this._surveyAllocation
    ).repair();
  }

  private _sortCriterias(criteria: Criteria[]): Criteria[] {
    return criteria.sort(
      (c1, c2) => this._criterionAtIndex(c1) - this._criterionAtIndex(c2)
    );
  }

  private _criterionAtIndex(criterion: Criteria): number {
    return this._selectedIds.findIndex(
      (selectedId) =>
        new SurveyQuotaCriterion(criterion).qualificationCode() === selectedId
    );
  }

  private _qualifications(): NestingQualification[] {
    return new NestedQualificationBuilder(
      this._surveyQualifications,
      this._surveyQuotas,
      this._surveyNestedQuotas.qualificationIds()
    ).build();
  }

  private _getSelectedQualifications(): NestingQualification[] {
    return this._qualifications()
      .filter((q) =>
        this._surveyNestedQuotas
          .qualificationIds()
          .includes(Number(q.qualification_code))
      )
      .sort(
        (q1, q2) =>
          this._qualificationOrder(q1.qualification_code) -
          this._qualificationOrder(q2.qualification_code)
      );
  }

  private _qualificationOrder(code: number): number {
    return this._surveyNestedQuotas
      .qualificationIds()
      .findIndex((qId) => Number(code) === Number(qId));
  }

  private _titleState(): NestingGroupTitleState {
    return 'view';
  }

  private _qualSelectable(): boolean {
    return this._state() === 'pending';
  }

  private _canResetTable(): boolean {
    return new NestedQuotasResetEvaluator(
      this._surveyQuotas,
      this._selectedIds,
      this._nestedQuotas
    ).evaluate();
  }
}
